import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatUSD, formatDate, formatApiError, todayISO } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Banknote, ClipboardSignature, ChevronRight, Plus, Pencil, Trash2, ChevronDown, ChevronUp, Eye } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import SignaturePad from "@/components/SignaturePad";

export default function CashOnHandPage() {
  const { activeOrgId } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const signingId = params.get("signing");

  const [cash, setCash] = useState({ total: 0, items: [] });
  const [pending, setPending] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signCount, setSignCount] = useState(null);
  const [signForm, setSignForm] = useState({ slot: 1, typed_name: "", signature_image: null });
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [reviewExpanded, setReviewExpanded] = useState(true);
  const [depOpen, setDepOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [depForm, setDepForm] = useState({ bank_name: "", reference: "", deposited_at: todayISO(), notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const deleteCount = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/organizations/${activeOrgId}/counts/${removeTarget.id}`);
      toast.success("Count deleted");
      setRemoveTarget(null);
      setSelected((s) => { const n = { ...s }; delete n[removeTarget.id]; return n; });
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); setRemoveTarget(null); }
  };

  const [signError, setSignError] = useState("");
  const [signKey, setSignKey] = useState(0); // bump to remount SignaturePad (clear ink)

  const fetchAll = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const [c, p, d] = await Promise.all([
        api.get(`/organizations/${activeOrgId}/cash-on-hand`),
        api.get(`/organizations/${activeOrgId}/counts?status=pending_sig`),
        api.get(`/organizations/${activeOrgId}/deposits`),
      ]);
      setCash(c.data);
      setPending(p.data);
      setDeposits(d.data.slice(0, 10));
    } finally { setLoading(false); }
  }, [activeOrgId]);

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [activeOrgId]);
  const location = useLocation();
  useEffect(() => { fetchAll(); }, [location.key, fetchAll]);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") fetchAll(); };
    window.addEventListener("focus", fetchAll);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", fetchAll);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchAll]);

  useEffect(() => {
    if (signingId && pending.length) {
      const target = pending.find((c) => c.id === signingId);
      if (target) setSignCount(target);
    }
  }, [signingId, pending]);

  const selectedItems = useMemo(() => cash.items.filter((c) => selected[c.id]), [selected, cash.items]);
  const selectedTotal = selectedItems.reduce((s, c) => s + (c.net_amount || 0), 0);

  const openSign = (c, slot) => {
    setSignCount(c);
    setSignForm({ slot, typed_name: "", signature_image: null });
    setReviewConfirmed(false);
    setReviewExpanded(true);
    setSignError("");
    setSignKey((k) => k + 1);
  };

  const submitSign = async () => {
    setSignError("");
    const typed = signForm.typed_name.trim();
    const expected = (signForm.slot === 1 ? signCount.counter_1_name : signCount.counter_2_name) || "";
    if (!typed) { setSignError("Please type your full name to sign."); return; }
    if (!signForm.signature_image) { setSignError("Please draw your signature on the pad below."); return; }
    if (!reviewConfirmed) { setSignError("Please review the count details and confirm they are accurate before signing."); return; }
    if (typed.toLowerCase() !== expected.trim().toLowerCase()) {
      setSignError(`Name mismatch — this slot is reserved for "${expected}". Please type exactly that name, or have the correct counter sign.`);
      return;
    }
    try {
      const { data: updated } = await api.post(`/organizations/${activeOrgId}/counts/${signCount.id}/sign`, {
        typed_name: typed,
        counter_slot: signForm.slot,
        signature_image: signForm.signature_image,
      });
      toast.success(`Signed by ${typed}`);
      const otherSlot = signForm.slot === 1 ? 2 : 1;
      const otherSigned = (updated.signatures || []).some((s) => s.counter_slot === otherSlot);
      if (updated.status === "verified" || otherSigned) {
        // both signed — finalize and close
        setSignCount(null);
        setSignForm({ slot: 1, typed_name: "", signature_image: null });
        toast.success("Count verified — moved to Cash on Hand");
      } else {
        // advance to the other counter, clear pad
        setSignCount(updated);
        // second slot signs next
        setSignForm({ slot: otherSlot, typed_name: "", signature_image: null });
        setReviewConfirmed(false);
        setReviewExpanded(true);
        setSignKey((k) => k + 1);
      }
      fetchAll();
    } catch (e) {
      const msg = formatApiError(e);
      setSignError(msg);
      toast.error(msg);
    }
  };

  const deleteDeposit = async (dep) => {
    if (!window.confirm(`Reverse deposit ${dep.bank_name}? Counts will return to Cash on Hand and the ledger entries will be removed.`)) return;
    try {
      await api.delete(`/organizations/${activeOrgId}/deposits/${dep.id}`);
      toast.success("Deposit reversed");
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitDeposit = async () => {
    if (!depForm.bank_name.trim()) return toast.error("Bank name is required");
    if (selectedItems.length === 0) return toast.error("Select at least one count");
    setSubmitting(true);
    try {
      await api.post(`/organizations/${activeOrgId}/deposits`, {
        count_ids: selectedItems.map((c) => c.id),
        bank_name: depForm.bank_name.trim(),
        reference: depForm.reference,
        deposited_at: depForm.deposited_at,
        notes: depForm.notes,
      });
      toast.success(`Deposited ${formatUSD(selectedTotal)} into ${depForm.bank_name}`);
      setDepOpen(false);
      setSelected({});
      setDepForm({ bank_name: "", reference: "", deposited_at: todayISO(), notes: "" });
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-5 fade-up">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Treasury</div>
        <h1 className="font-serif text-3xl tracking-tight mt-1">Cash on hand & deposits</h1>
      </div>

      <Card className="rounded-2xl border-border shadow-none p-5 bg-primary/5">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Total cash on hand</div>
        <div className="font-mono text-3xl mt-1 text-primary tabular-nums" data-testid="cash-onhand-total">{formatUSD(cash.total)}</div>
        <div className="text-xs text-muted-foreground mt-1">{cash.items.length} verified count{cash.items.length === 1 ? "" : "s"} awaiting deposit</div>
      </Card>

      {pending.length > 0 && (
        <Card className="rounded-2xl border-amber-200 bg-amber-50/40 shadow-none overflow-hidden">
          <div className="px-5 py-3 border-b border-amber-200 flex items-center gap-2">
            <ClipboardSignature className="w-4 h-4 text-amber-700" />
            <div className="text-sm font-semibold text-amber-800">Pending signatures · {pending.length}</div>
          </div>
          <div className="divide-y divide-amber-200/60">
            {pending.map((c) => {
              const sig1 = (c.signatures || []).find((s) => s.counter_slot === 1);
              const sig2 = (c.signatures || []).find((s) => s.counter_slot === 2);
              return (
                <div key={c.id} className="px-5 py-3" data-testid={`pending-count-${c.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.counter_1_name} & {c.counter_2_name}</div>
                      <div className="text-xs text-muted-foreground">{formatDate(c.date)}</div>
                    </div>
                    <div className="font-mono tabular-nums">{formatUSD(c.total_amount)}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <Button
                      variant={sig1 ? "outline" : "default"}
                      size="sm"
                      disabled={!!sig1}
                      className="rounded-xl"
                      onClick={() => openSign(c, 1)}
                      data-testid={`sign-counter1-${c.id}`}
                    >
                      {sig1 ? `✓ ${c.counter_1_name}` : `Sign as ${c.counter_1_name}`}
                    </Button>
                    <Button
                      variant={sig2 ? "outline" : "default"}
                      size="sm"
                      disabled={!!sig2}
                      className="rounded-xl"
                      onClick={() => openSign(c, 2)}
                      data-testid={`sign-counter2-${c.id}`}
                    >
                      {sig2 ? `✓ ${c.counter_2_name}` : `Sign as ${c.counter_2_name}`}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Button size="sm" variant="ghost" className="rounded-xl flex-1" onClick={() => nav(`/m/count?edit=${c.id}`)} data-testid={`edit-count-${c.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-xl flex-1 text-destructive hover:bg-destructive/10" onClick={() => setRemoveTarget(c)} data-testid={`delete-count-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card className="rounded-2xl border-border shadow-none overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="text-sm font-semibold flex items-center gap-2"><Banknote className="w-4 h-4 text-muted-foreground" /> Ready to deposit</div>
          <Link to="/m/count" className="text-xs text-primary"><Plus className="w-3.5 h-3.5 inline mr-0.5" />New count</Link>
        </div>
        {loading ? (
          <div className="p-5 text-sm text-muted-foreground">Loading…</div>
        ) : cash.items.length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground">No verified counts waiting. Once both counters sign, the count moves here.</div>
        ) : (
          <>
            <div className="divide-y divide-border">
              {cash.items.map((c) => (
                <div key={c.id} className="px-5 py-3 flex items-center gap-3 hover:bg-secondary/40" data-testid={`coh-row-${c.id}`}>
                  <Checkbox
                    checked={!!selected[c.id]}
                    onCheckedChange={(v) => setSelected((s) => ({ ...s, [c.id]: !!v }))}
                    data-testid={`coh-check-${c.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{c.counter_1_name} & {c.counter_2_name}</div>
                    <div className="text-xs text-muted-foreground">{formatDate(c.date)} · cash {formatUSD(c.cash_total)} · checks {formatUSD(c.checks_total)} · cards {formatUSD(c.cards_total)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono tabular-nums">{formatUSD(c.net_amount)}</div>
                    {c.cards_fees > 0 && <div className="text-[10px] text-muted-foreground">-{formatUSD(c.cards_fees)} fees</div>}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <Button size="icon" variant="ghost" className="rounded-xl h-8 w-8" onClick={() => nav(`/m/count?edit=${c.id}`)} data-testid={`coh-edit-${c.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="rounded-xl h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => setRemoveTarget(c)} data-testid={`coh-delete-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 bg-secondary/40 border-t border-border flex items-center justify-between">
              <div className="text-sm">
                <span className="text-muted-foreground">Selected</span>{" "}
                <span className="font-mono tabular-nums font-semibold">{formatUSD(selectedTotal)}</span>
                <span className="text-muted-foreground"> · {selectedItems.length} count{selectedItems.length === 1 ? "" : "s"}</span>
              </div>
              <Button disabled={selectedItems.length === 0} onClick={() => setDepOpen(true)} className="rounded-xl soft-glow" data-testid="create-deposit-button">
                Deposit selected <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </>
        )}
      </Card>

      {deposits.length > 0 && (
        <Card className="rounded-2xl border-border shadow-none overflow-hidden">
          <div className="px-5 py-3 border-b border-border">
            <div className="text-sm font-semibold">Recent deposits</div>
          </div>
          <div className="divide-y divide-border">
            {deposits.map((d) => (
              <div key={d.id} className="px-5 py-3 flex items-center justify-between gap-2" data-testid={`deposit-row-${d.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{d.bank_name} {d.reference && <span className="text-muted-foreground">· {d.reference}</span>}</div>
                  <div className="text-xs text-muted-foreground">{formatDate(d.deposited_at)} · by {d.deposited_by_name || "—"} · {d.count_ids.length} count{d.count_ids.length === 1 ? "" : "s"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono tabular-nums text-primary">{formatUSD(d.net_amount)}</div>
                  {d.fees > 0 && <div className="text-[10px] text-muted-foreground">-{formatUSD(d.fees)} fees</div>}
                </div>
                <Button size="icon" variant="ghost" className="rounded-xl h-8 w-8 text-destructive hover:bg-destructive/10 shrink-0" onClick={() => deleteDeposit(d)} data-testid={`deposit-delete-${d.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Signature dialog */}
      <Dialog open={!!signCount} onOpenChange={(o) => { if (!o) { setSignCount(null); setReviewConfirmed(false); } }}>
        <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="sign-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Review &amp; sign</DialogTitle>
            <DialogDescription>
              Review every line below. Once you sign, this becomes your legal attestation that the count is accurate.
            </DialogDescription>
          </DialogHeader>
          {signCount && (
            <div className="space-y-4 py-2">
              <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Signing as counter slot {signForm.slot}</div>
                <div className="font-serif text-xl mt-1">
                  {signForm.slot === 1 ? signCount.counter_1_name : signCount.counter_2_name}
                </div>
                <div className="text-sm text-muted-foreground mt-1">Grand total: <span className="font-mono tabular-nums text-foreground font-semibold">{formatUSD(signCount.total_amount)}</span></div>
              </div>

              {/* Review — collapsible, expanded by default so signers must scroll past. */}
              <div className="rounded-2xl border border-border overflow-hidden" data-testid="sign-review-block">
                <button
                  type="button"
                  onClick={() => setReviewExpanded(!reviewExpanded)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-secondary/50 hover:bg-secondary transition-colors"
                  data-testid="sign-review-toggle"
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <Eye className="w-4 h-4 text-primary" /> Count details to review
                  </span>
                  {reviewExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>
                {reviewExpanded && (
                  <div className="p-4 space-y-3 text-xs">
                    {/* Bill counter breakdown */}
                    {(() => {
                      const cd = signCount.cash_denominations || {};
                      const denoms = [
                        { key: "hundreds", label: "$100", value: 100 },
                        { key: "fifties",  label: "$50",  value: 50 },
                        { key: "twenties", label: "$20",  value: 20 },
                        { key: "tens",     label: "$10",  value: 10 },
                        { key: "fives",    label: "$5",   value: 5 },
                        { key: "twos",     label: "$2",   value: 2 },
                        { key: "ones",     label: "$1",   value: 1 },
                      ];
                      const total = denoms.reduce((s, d) => s + (Number(cd[d.key]) || 0) * d.value, 0);
                      const coins = Number(signCount.coins_amount) || 0;
                      if (total === 0 && coins === 0) return null;
                      return (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 flex justify-between">
                            <span>Bill counter</span>
                            <span className="normal-case tracking-normal text-foreground font-mono tabular-nums">{formatUSD(total + coins)}</span>
                          </div>
                          <div className="grid grid-cols-7 gap-1">
                            {denoms.map((d) => {
                              const qty = Number(cd[d.key]) || 0;
                              return (
                                <div key={d.key} className={`rounded-lg py-1 text-center ${qty > 0 ? "bg-primary/5" : "bg-secondary/40 opacity-50"}`}>
                                  <div className="text-[9px] uppercase text-muted-foreground">{d.label}</div>
                                  <div className="font-mono tabular-nums text-[11px]">×{qty}</div>
                                </div>
                              );
                            })}
                          </div>
                          {coins > 0 && (
                            <div className="flex justify-between text-[11px] pt-1.5 mt-1.5 border-t border-border">
                              <span className="text-muted-foreground">Coins</span>
                              <span className="font-mono tabular-nums">{formatUSD(coins)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Checks & envelopes */}
                    {(signCount.checks || []).length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Envelopes &amp; checks · {signCount.checks.length}</div>
                        <div className="rounded-xl bg-secondary/30 divide-y divide-border" data-testid="sign-review-checks">
                          {signCount.checks.map((ch, i) => {
                            const isCash = (ch.payment_type || "").toLowerCase() === "cash" || !(ch.check_number || "").trim();
                            return (
                              <div key={i} className="p-2 leading-tight">
                                <div className="flex justify-between gap-2">
                                  <span className="min-w-0 truncate">
                                    {ch.donor_name || <span className="italic text-muted-foreground">Anonymous</span>}
                                    <span className="text-muted-foreground"> · </span>
                                    <span className="text-primary">{ch.category_name || (isCash ? "General Offering" : "Tithes")}</span>
                                    {!isCash && (ch.check_number || "").trim() && <span className="text-muted-foreground"> · #{ch.check_number}</span>}
                                  </span>
                                  <span className="font-mono tabular-nums shrink-0">{formatUSD(ch.amount)}</span>
                                </div>
                                {(ch.notes || "").trim() && (
                                  <div className="mt-1 pl-2 text-[10px] text-muted-foreground italic border-l-2 border-primary/30">&ldquo;{ch.notes}&rdquo;</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Cards */}
                    {(signCount.cards || []).length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Card gifts · {signCount.cards.length}</div>
                        <div className="rounded-xl bg-secondary/30 divide-y divide-border" data-testid="sign-review-cards">
                          {signCount.cards.map((cd, i) => (
                            <div key={i} className="p-2 leading-tight">
                              <div className="flex justify-between gap-2">
                                <span className="min-w-0 truncate">
                                  {cd.donor_name || <span className="italic text-muted-foreground">Anonymous</span>}
                                  <span className="text-muted-foreground"> · </span>
                                  <span className="text-primary">{cd.category_name || "Tithes"}</span>
                                  {cd.last4 && <span className="text-muted-foreground"> · &bull;&bull;&bull;&bull; {cd.last4}</span>}
                                  <span className="text-muted-foreground"> (fee {formatUSD(cd.fee)})</span>
                                </span>
                                <span className="font-mono tabular-nums shrink-0">{formatUSD(cd.amount)}</span>
                              </div>
                              {(cd.notes || "").trim() && (
                                <div className="mt-1 pl-2 text-[10px] text-muted-foreground italic border-l-2 border-primary/30">&ldquo;{cd.notes}&rdquo;</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Totals row */}
                    <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border">
                      <div className="text-center">
                        <div className="text-[9px] uppercase text-muted-foreground">Cash</div>
                        <div className="font-mono tabular-nums text-xs">{formatUSD(signCount.cash_total)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[9px] uppercase text-muted-foreground">Checks</div>
                        <div className="font-mono tabular-nums text-xs">{formatUSD(signCount.checks_total)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[9px] uppercase text-muted-foreground">Cards</div>
                        <div className="font-mono tabular-nums text-xs">{formatUSD(signCount.cards_total)}</div>
                      </div>
                    </div>
                    {(signCount.notes || "").trim() && (
                      <div className="pt-2 border-t border-border">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Count notes</div>
                        <div className="text-[11px] italic text-foreground/80">&ldquo;{signCount.notes}&rdquo;</div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Attestation checkbox */}
              <label className="flex items-start gap-2 p-3 rounded-2xl bg-secondary/40 cursor-pointer">
                <Checkbox
                  checked={reviewConfirmed}
                  onCheckedChange={(v) => setReviewConfirmed(v === true)}
                  className="mt-0.5"
                  data-testid="sign-review-confirm"
                />
                <span className="text-xs leading-relaxed text-foreground/90">
                  I've reviewed every line above and confirm the totals and details are accurate to the best of my knowledge.
                </span>
              </label>

              <div className="space-y-1.5">
                <Label>Type your full name to sign <span className="text-destructive">*</span></Label>
                <Input
                  value={signForm.typed_name}
                  onChange={(e) => setSignForm({ ...signForm, typed_name: e.target.value })}
                  placeholder={signForm.slot === 1 ? signCount.counter_1_name : signCount.counter_2_name}
                  className="rounded-xl h-12 font-serif text-xl italic"
                  data-testid="sign-typed-input"
                />
                <p className="text-[10px] text-muted-foreground">Name must match the counter slot exactly.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Draw your signature <span className="text-destructive">*</span></Label>
                <SignaturePad
                  key={`sigpad-${signCount.id}-${signForm.slot}-${signKey}`}
                  onChange={(img) => setSignForm((f) => ({ ...f, signature_image: img }))}
                  testid="sign-pad"
                />
              </div>
              {signError && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 text-destructive text-xs p-2.5" data-testid="sign-error">
                  {signError}
                </div>
              )}
              {/* Sticky footer so the attestation + Sign button stay visible on
                  mobile after the signer scrolls past the review + signature pad. */}
              <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-card border-t border-border">
                <label className="flex items-start gap-2 mb-3 cursor-pointer">
                  <Checkbox
                    checked={reviewConfirmed}
                    onCheckedChange={(v) => setReviewConfirmed(v === true)}
                    className="mt-0.5"
                    data-testid="sign-review-confirm-sticky"
                  />
                  <span className="text-[11px] leading-relaxed text-foreground/90">
                    I confirm the totals and details above are accurate.
                  </span>
                </label>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" className="rounded-xl" onClick={() => setSignCount(null)}>Cancel</Button>
                  <Button
                    onClick={submitSign}
                    className="rounded-xl soft-glow"
                    disabled={!reviewConfirmed}
                    data-testid="sign-submit-button"
                  >
                    <ClipboardSignature className="w-4 h-4 mr-2" /> Sign
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deposit dialog */}
      <Dialog open={depOpen} onOpenChange={setDepOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="deposit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Create deposit batch</DialogTitle>
            <DialogDescription>
              {selectedItems.length} count{selectedItems.length === 1 ? "" : "s"} · {formatUSD(selectedTotal)} net. These will move from cash-on-hand into the ledger.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Bank name <span className="text-destructive">*</span></Label>
              <Input value={depForm.bank_name} onChange={(e) => setDepForm({ ...depForm, bank_name: e.target.value })} placeholder="e.g. First National Bank" className="rounded-xl h-11" data-testid="deposit-bank-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Reference / slip #</Label>
                <Input value={depForm.reference} onChange={(e) => setDepForm({ ...depForm, reference: e.target.value })} placeholder="Optional" className="rounded-xl h-11" data-testid="deposit-ref-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={depForm.deposited_at} onChange={(e) => setDepForm({ ...depForm, deposited_at: e.target.value })} className="rounded-xl h-11" data-testid="deposit-date-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea rows={2} value={depForm.notes} onChange={(e) => setDepForm({ ...depForm, notes: e.target.value })} className="rounded-xl" placeholder="Optional" />
            </div>
            <DialogFooter>
              <Button variant="ghost" className="rounded-xl" onClick={() => setDepOpen(false)}>Cancel</Button>
              <Button onClick={submitDeposit} disabled={submitting} className="rounded-xl soft-glow" data-testid="deposit-submit-button">
                {submitting ? "Depositing…" : "Confirm deposit"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
        title={`Delete count by ${removeTarget?.counter_1_name} & ${removeTarget?.counter_2_name}?`}
        description={`Total ${removeTarget ? "$" + Number(removeTarget.total_amount || 0).toFixed(2) : ""}. Counts that are already deposited can't be deleted.`}
        confirmLabel="Delete count"
        onConfirm={deleteCount}
        testid="confirm-delete-count"
      />
    </div>
  );
}
